import React, { useState, useEffect } from "react";
import Text from "./Text";
import BaseButton from "./BaseButton";
import Skeleton from "./Skeleton";
import { MdOutlineArrowBackIos, MdRoute, MdOutlineElevator, MdOutlineHome } from "react-icons/md";
import { useTranslation } from "react-i18next";

const FACILITY_PHOTO_IDS = new Set(__FACILITY_PHOTO_IDS__);

type SidebarFacilityInformationProps = {
  itemId: string;
  floorName: string;
  roomLabel: string;
  roomSubLabel?: string;
  description?: string;
  hasOstomate?: boolean;
  onBack?: () => void;
  onGoHome?: () => void; // ホームへ戻る
  onViewDefaultRoute?: () => void;
  onViewBarrierfreeRoute?: () => void;
  isElevatorButtonVisible?: boolean;
  isMobile?: boolean;
  className?: string;
  containerRef?: React.RefObject<HTMLDivElement | null>;
};

const SidebarFacilityInformation: React.FC<SidebarFacilityInformationProps> = ({
  itemId,
  floorName,
  roomLabel,
  roomSubLabel,
  description,
  hasOstomate = false,
  onBack,
  onGoHome,
  onViewDefaultRoute,
  onViewBarrierfreeRoute,
  isElevatorButtonVisible = true,
  isMobile = false,
  className = "",
  containerRef,
}) => {
  // 翻訳インスタンスのインポート
  const { t } = useTranslation();

  const baseClasses = [
    "bg-white",
    "box-border",
    "flex",
    "flex-col",
    isMobile ? "gap-4" : "gap-6",
    "items-start",
    "p-4",
    "sm:p-10",
    "relative",
    "w-full",
    "h-auto",
    "max-h-full",
    "sm:h-full",
    "overflow-y-auto",
    "shrink-0",
  ].join(" ");

  const photoPath = `/photos/${itemId}.JPG`;
  const hasPhoto = FACILITY_PHOTO_IDS.has(itemId);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [itemId]);

  return (
    <div ref={containerRef} className={`${baseClasses} ${className}`}>
      {/* 戻る / ホーム ボタン */}
      {(onBack || onGoHome) && (
        <div className="flex items-center justify-between w-full relative shrink-0 mb-2">
          {onBack && (
            <BaseButton onClick={onBack} icon={MdOutlineArrowBackIos}>
              {t("戻る")}
            </BaseButton>
          )}
          {onGoHome && (
            <BaseButton onClick={onGoHome} icon={MdOutlineHome} textVariant="body/s">
              {t("最初の画面に戻る")}
            </BaseButton>
          )}
        </div>
      )}
      {/* コンテンツ */}
      <div
        className={`flex flex-col items-start relative shrink-0 w-full ${isMobile ? "gap-4" : "gap-8"}`}
      >
        {/* 施設情報セクション */}
        <div
          className={`flex flex-col items-start not-italic relative shrink-0 w-full ${
            isMobile ? "gap-2" : "gap-4"
          }`}
        >
          <div className="flex flex-col items-start relative shrink-0 w-full">
            <Text as="p" variant="body/m" weight="regular">
              {floorName}
            </Text>
            <Text as="p" variant="heading/m" weight="bold">
              {roomLabel}
            </Text>

            {roomSubLabel && (
              <Text as="p" variant="body/s" weight="regular">
                {roomSubLabel}
              </Text>
            )}
          </div>
          {description && (
            <Text as="p" variant="body/m" weight="regular">
              {description}
            </Text>
          )}
          {/* オストメイト対応バッジ */}
          {hasOstomate && (
            <div className="flex flex-row items-center gap-3">
              <img
                src="/icons/ostomate.svg"
                alt={t("オストメイト設備あり")}
                width={40}
                height={40}
              />
              <Text as="span" variant="body/m" weight="bold">
                {t("オストメイト設備あり")}
              </Text>
            </div>
          )}
        </div>

        {/* 経路探索開始ボタン*/}
        <div className={`flex flex-col items-start relative shrink-0 w-full gap-2`}>
          {/* "経路を見る" (デフォルト経路) */}
          <BaseButton
            icon={MdRoute}
            onClick={onViewDefaultRoute}
            isSelected={true}
            className="w-full"
          >
            {t("経路を見る")}
          </BaseButton>

          {/* "バリアフリー経路" (エレベーター優先) */}
          {isElevatorButtonVisible && (
            <BaseButton
              icon={MdOutlineElevator}
              onClick={onViewBarrierfreeRoute}
              isSelected={false}
              className="w-full"
            >
              {t("エレベーターを使う経路を見る")}
            </BaseButton>
          )}
        </div>

        {/* 施設写真セクション (写真のある施設のみ。読み込み中はスケルトンで枠を確保する) */}
        {hasPhoto && !imageError && (
          <div className="border border-Border flex flex-col h-40 sm:h-56 items-center justify-center relative rounded-2xl shrink-0 w-full overflow-hidden">
            {!imageLoaded && <Skeleton className="absolute inset-0" />}
            <img
              src={photoPath}
              alt={t("施設写真")}
              loading="eager"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-200 ${
                imageLoaded ? "opacity-100" : "opacity-0"
              }`}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SidebarFacilityInformation;
